import { create } from 'zustand';

import {
  EngagementUpdateDTO,
  NewUpdateMode,
  ProgressResponseSignalOptionDto,
  SignalPeriodDto,
  SocialFeedSortType
} from '@/features/engagement/engagement.types';

type SelectedSignalContext = {
  progressResponseId: string;
  questionId: string;
  focusAreaId: string;
  businessUnitId: string;
  signal: string;
  type?: string;
  responseOptions: ProgressResponseSignalOptionDto[];
  businessUnits?: ProgressResponseSignalOptionDto[];
  responsePeriod?: string;
  availablePeriods?: SignalPeriodDto[];
};

type SelectedDeliverableContext = {
  deliverableId: string;
  deliverableName: string;
  focusAreaId: string;
};

type SelectedQuestionContext = {
  questionId: string;
  questionName: string;
  focusAreaId: string;
};

interface SocialFeedStore {
  selectedFocusAreaId: string | null;
  setSelectedFocusAreaId: (focusAreaId: string | null) => void;
  selectedBusinessUnitIds: string[];
  setSelectedBusinessUnitIds: (businessUnitIds: string[]) => void;
  selectedGroupIds: string[];
  setSelectedGroupIds: (groupIds: string[]) => void;
  selectedReportTypeIds: string[];
  setSelectedReportTypeIds: (reportTypeIds: string[]) => void;
  selectedTimePeriodId: string | null;
  setSelectedTimePeriodId: (timePeriodId: string | null) => void;
  selectedCustomDateFrom: Date | null;
  setSelectedCustomDateFrom: (date: Date | null) => void;
  selectedCustomDateTo: Date | null;
  setSelectedCustomDateTo: (date: Date | null) => void;
  defaultCreatePostFocusAreaId: string | null;
  setDefaultCreatePostFocusAreaId: (focusAreaId: string | null) => void;
  sortBy: SocialFeedSortType;
  setSortBy: (sortBy: SocialFeedSortType) => void;
  selectedSignal: SelectedSignalContext | null;
  selectedDeliverable: SelectedDeliverableContext | null;
  selectedQuestion: SelectedQuestionContext | null;
  setSelectedDeliverable: (selectedDeliverable: SelectedDeliverableContext | null) => void;
  selectedEngagePostForEdit: EngagementUpdateDTO | null;
  isCreatePostModalOpen: boolean;
  postModalMode: NewUpdateMode;
  openCreatePostModal: (
    mode: NewUpdateMode,
    selectedSignal?: SelectedSignalContext,
    selectedDeliverable?: SelectedDeliverableContext,
    selectedQuestion?: SelectedQuestionContext
  ) => void;
  openEditPostModal: (post: EngagementUpdateDTO) => void;
  openEditSignalResponseModal: (post: EngagementUpdateDTO) => void;
  closeCreatePostModal: () => void;
}

export const useSocialFeedStore = create<SocialFeedStore>()((set) => ({
  selectedFocusAreaId: null,
  setSelectedFocusAreaId: (focusAreaId) => {
    set({ selectedFocusAreaId: focusAreaId });
  },
  selectedBusinessUnitIds: [],
  setSelectedBusinessUnitIds: (businessUnitIds) => {
    set({ selectedBusinessUnitIds: businessUnitIds });
  },
  selectedGroupIds: [],
  setSelectedGroupIds: (groupIds) => {
    set({ selectedGroupIds: groupIds });
  },
  selectedReportTypeIds: [],
  setSelectedReportTypeIds: (reportTypeIds) => {
    set({ selectedReportTypeIds: reportTypeIds });
  },
  selectedTimePeriodId: null,
  setSelectedTimePeriodId: (timePeriodId) => {
    set({ selectedTimePeriodId: timePeriodId });
  },
  selectedCustomDateFrom: null,
  setSelectedCustomDateFrom: (date) => {
    set({ selectedCustomDateFrom: date });
  },
  selectedCustomDateTo: null,
  setSelectedCustomDateTo: (date) => {
    set({ selectedCustomDateTo: date });
  },
  defaultCreatePostFocusAreaId: null,
  setDefaultCreatePostFocusAreaId: (focusAreaId) => {
    set({ defaultCreatePostFocusAreaId: focusAreaId });
  },
  sortBy: 'Latest',
  setSortBy: (sortBy) => {
    set({ sortBy });
  },
  selectedSignal: null,
  selectedDeliverable: null,
  selectedQuestion: null,
  setSelectedDeliverable: (selectedDeliverable) => {
    set({ selectedDeliverable });
  },
  selectedEngagePostForEdit: null,
  isCreatePostModalOpen: false,
  postModalMode: 'text',
  openCreatePostModal: (mode, selectedSignal, selectedDeliverable, selectedQuestion) => {
    set({
      isCreatePostModalOpen: true,
      postModalMode: mode,
      selectedSignal: selectedSignal || null,
      selectedDeliverable: selectedDeliverable || null,
      selectedQuestion: selectedQuestion || null,
      selectedEngagePostForEdit: null
    });
  },
  openEditPostModal: (post) => {
    set({
      isCreatePostModalOpen: true,
      postModalMode: 'text',
      selectedSignal: null,
      selectedDeliverable: null,
      selectedQuestion: null,
      selectedEngagePostForEdit: post
    });
  },
  openEditSignalResponseModal: (post) => {
    const progressResponse = post.progressResponse;
    const responseOptions =
      progressResponse?.progressResponseOptions?.map((option) => ({
        id: option.clientContributionResponseOptionId,
        title: option.title
      })) || [];

    set({
      isCreatePostModalOpen: true,
      postModalMode: 'text',
      selectedSignal: {
        progressResponseId: post.id,
        questionId: progressResponse?.contribution?.id || '',
        focusAreaId: post.focusArea.id,
        businessUnitId: post.businessUnitId || post.targetBusinessUnitId || '',
        signal: progressResponse?.contribution?.name || post.content || '',
        type: progressResponse?.contribution?.type,
        responseOptions,
        businessUnits: post.businessUnits || [],
        responsePeriod: progressResponse?.responsePeriod,
        availablePeriods: progressResponse?.responsePeriod
          ? [
              {
                progressResponseId: post.id,
                businessUnitId: post.businessUnitId || post.targetBusinessUnitId || '',
                scheduledPeriod: progressResponse.responsePeriod
              }
            ]
          : []
      },
      selectedDeliverable: null,
      selectedQuestion: null,
      selectedEngagePostForEdit: post
    });
  },
  closeCreatePostModal: () => {
    set({
      isCreatePostModalOpen: false,
      selectedSignal: null,
      selectedDeliverable: null,
      selectedQuestion: null,
      selectedEngagePostForEdit: null
    });
  }
}));
